/* eslint-disable */
import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  CircularProgress,
  Button,
} from '@mui/material'

// Helpers
import { sortUsers } from '../../pages/searchUser/components/usersList/usersList.helpers'
import { sortGroups } from '../../pages/searchGroup/components/groupsList/groupsList.helpers'
import { getStatus, getLastLogin } from '../../helpers/users'

// Hook
import { useIsMobileView } from '../../utils/isMobileView'

// Assets
import LockClosedIcon from '../../assets/LockClosed.svg'

// Styles (Desktop & Mobile)
import styles from './EntityList.module.scss'

// type: 'user' | 'group'
const EntityList = ({ type, items, areLoading }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isMobileView = useIsMobileView()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortBy, setSortBy] = useState(type === 'user' ? 'firstName' : 'displayName')
  const [sortOrder, setSortOrder] = useState('asc')

  useEffect(() => {
    setPage(0)
  }, [items])

  const handleChangePage = (_, newPage) => setPage(newPage)

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const handleSort = property => {
    const newOrder = sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc'
    setSortBy(property)
    setSortOrder(newOrder)
  }

  const sortedItems = useMemo(() => {
    if (type === 'user') {
      return sortUsers({ users: items || [], sortBy, sortOrder })
    }
    return sortGroups({ groups: items || [], sortBy, sortOrder })
  }, [items, sortBy, sortOrder, type])

  const handleView = id => {
    if (type === 'user') navigate(`/view-user/${id}`)
    else navigate(`/view-group/${id}`)
  }

  const handleEdit = (e, id) => {
    e.stopPropagation()
    if (type === 'user') navigate(`/edit-user/${id}`)
    else navigate(`/edit-group/${id}`)
  }

  const handleEditMembers = (e, id) => {
    if (type !== 'group') return
    e.stopPropagation()
    navigate(`/edit-members/${id}`)
  }

  const editStyleUser = {
    backgroundColor: '#eb1700',
    '&:hover': { backgroundColor: '#CC1400' },
    '&:disabled': { backgroundColor: '#665A47' },
    '&:focus': { backgroundColor: '#CC1400' },
  }

  const editStyleGroup = {
    backgroundColor: '#eb1700',
    '&:hover': { backgroundColor: '#CC1400' },
    '&:disabled': { backgroundColor: '#665A47' },
    '&:focus': { backgroundColor: '#CC1400' },
    fontFamily: 'Johnson Text',
    fontWeight: 400,
    fontSize: '12px',
  }

  // ======== DESKTOP ========
  const renderTableHeadDesktop = () => {
    if (type === 'user') {
      return (
        <TableRow sx={{ '& th': { backgroundColor: '#F5F5F5' } }}>
          <TableCell className={styles.headerCellDesktop}>
            <TableSortLabel className={styles.headerLabelDesktop} />
          </TableCell>
          <TableCell className={styles.headerCellDesktop}>
            <TableSortLabel
              className={styles.headerLabelDesktop}
              active={sortBy === 'firstName'}
              direction={sortOrder}
              onClick={() => handleSort('firstName')}
            >
              {t('first-name')}
            </TableSortLabel>
          </TableCell>
          <TableCell className={styles.headerCellDesktop}>
            <TableSortLabel
              className={styles.headerLabelDesktop}
              active={sortBy === 'lastName'}
              direction={sortOrder}
              onClick={() => handleSort('lastName')}
            >
              {t('last-name')}
            </TableSortLabel>
          </TableCell>
          <TableCell className={styles.headerCellDesktop}>
            <TableSortLabel
              className={styles.headerLabelDesktop}
              active={sortBy === 'email'}
              direction={sortOrder}
              onClick={() => handleSort('email')}
            >
              {t('email')}
            </TableSortLabel>
          </TableCell>
          <TableCell className={styles.headerCellDesktop}>
            <TableSortLabel
              className={styles.headerLabelDesktop}
              active={sortBy === 'status'}
              direction={sortOrder}
              onClick={() => handleSort('status')}
            >
              {t('status')}
            </TableSortLabel>
          </TableCell>
          <TableCell className={styles.headerCellDesktop}>
            {t('last-login-table-header')}
          </TableCell>
          <TableCell className={styles.headerCellDesktop} />
          <TableCell className={styles.headerCellDesktop} />
        </TableRow>
      )
    }

    return (
      <TableRow sx={{ '& th': { backgroundColor: '#F5F5F5' } }}>
        <TableCell className={styles.headerCellDesktop}>
          {t('view-edit-column-group-id')}
        </TableCell>
        <TableCell className={styles.headerCellDesktop}>
          <TableSortLabel
            className={styles.headerLabelDesktop}
            active={sortBy === 'displayName'}
            direction={sortOrder}
            onClick={() => handleSort('displayName')}
          >
            {t('view-edit-column-group-name')}
          </TableSortLabel>
        </TableCell>
        <TableCell className={styles.headerCellDesktop}>
          {t('view-edit-column-group-update-description')}
        </TableCell>
        <TableCell className={styles.headerCellDesktop}>
          {t('view-edit-column-group-edit-members')}
        </TableCell>
      </TableRow>
    )
  }

  const renderTableBodyDesktop = () => {
    if (type === 'user') {
      return sortedItems
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(user => (
          <TableRow
            className={styles.tableBodyRowDesktop}
            key={user.id}
            onClick={event => {
              event.stopPropagation()
              if (event.target?.nodeName?.toLowerCase() === 'td') {
                handleView(user.id)
              }
            }}
          >
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {getStatus(user) === 'LOCKED_OUT' && (
                <img
                  src={LockClosedIcon}
                  alt=""
                  style={{ marginBottom: '4px', width: '24px', height: '24px' }}
                />
              )}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {user.name?.givenName}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {user.name?.familyName}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {user.emails?.[0]?.value}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {getStatus(user)}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {getLastLogin(user)}
            </TableCell>
            <TableCell style={{ marginRight: '30px' }} />
            <TableCell sx={{ fontSize: '16px' }}>
              <Button
                onClick={e => handleEdit(e, user.id)}
                variant="contained"
                className="primary-btn"
                disableElevation
                sx={{ ...editStyleUser }}
              >
                {t('edit')}
              </Button>
            </TableCell>
          </TableRow>
        ))
    }

    return sortedItems
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map(group => (
        <TableRow
          className={styles.tableBodyRowDesktop}
          key={group.id}
          onClick={event => {
            event.stopPropagation()
            if (event.target?.nodeName?.toLowerCase() === 'td') {
              handleView(group.id)
            }
          }}
        >
          <TableCell sx={{ fontSize: '16px', color: '#000' }} className={styles.tableCellDesktop}>
            {group.id}
          </TableCell>
          <TableCell sx={{ fontSize: '16px', color: '#000' }} className={styles.tableCellDesktop}>
            {group.displayName}
          </TableCell>
          <TableCell sx={{ fontSize: '16px' }}>
            <Button
              onClick={e => handleEdit(e, group.id)}
              variant="contained"
              className="primary-btn"
              disableElevation
              sx={{ ...editStyleGroup }}
            >
              {t('view-edit-column-group-edit-button')}
            </Button>
          </TableCell>
          <TableCell sx={{ fontSize: '16px' }}>
            <Button
              onClick={e => handleEditMembers(e, group.id)}
              variant="contained"
              className="primary-btn"
              disableElevation
              sx={{
                color: 'rgba(0, 0, 0, 0.87)',
                width: '155px',
                backgroundColor: 'rgba(98, 81, 55, 0.1)',
                '&:hover': { backgroundColor: 'rgba(102, 90, 71, 0.20)' },
                '&:disabled': { backgroundColor: '#665A47' },
                '&:focus': { backgroundColor: 'rgba(102, 90, 71, 0.20)' },
                fontFamily: 'Johnson Text',
                fontWeight: 600,
                fontSize: '12px',
              }}
            >
              {t('view-edit-column-group-member-button')}
            </Button>
          </TableCell>
        </TableRow>
      ))
  }

  const renderDesktop = () => (
    <div className={styles.containerDesktop}>
      {(areLoading || (items && items.length > 0)) && (
        <div>
          <p className={styles.resultsDesktop}>
            {items?.length || 0} {t('results')}
          </p>
          <TableContainer className={styles.tableContainerDesktop}>
            <Table stickyHeader className={styles.tableDesktop}>
              <TableHead>{renderTableHeadDesktop()}</TableHead>
              <TableBody>
                {areLoading ? (
                  <TableRow>
                    <TableCell sx={{ border: 'none' }} colSpan={6}>
                      <div className={styles.loaderContainerDesktop}>
                        <div className={styles.loadingDesktop}>{t('loading')}</div>
                        <CircularProgress sx={{ color: 'black' }} />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  renderTableBodyDesktop()
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {(items?.length || 0) > 10 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={items?.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </div>
      )}
    </div>
  )

  // ======== MOBILE ========
  const renderCardMobileUser = user => {
    const locked = getStatus(user) === 'LOCKED_OUT'
    return (
      <div
        key={user.id}
        className={styles.cardMobile}
        tabIndex={0}
      >
        {locked && (
          <div className={styles.lockedInfoMobile}>
            <img src={LockClosedIcon} alt="" className={styles.lockedIconMobile} />
            <span className={styles.lockedTextMobile}>{t('search-tip-user-locked')}</span>
          </div>
        )}

        <div className={styles.fieldBlockMobile}>
          <div className={styles.fieldLabelMobile}>{t('first-name')}</div>
          <div className={styles.fieldValueMobile}>{user.name?.givenName || '-'}</div>
        </div>

        <div className={styles.fieldBlockMobile}>
          <div className={styles.fieldLabelMobile}>{t('last-name')}</div>
          <div className={styles.fieldValueMobile}>{user.name?.familyName || '-'}</div>
        </div>

        <div className={styles.fieldBlockMobile}>
          <div className={styles.fieldLabelMobile}>{t('email')}</div>
          <div className={styles.fieldValueMobile}>{user.emails?.[0]?.value || '-'}</div>
        </div>

        <div className={styles.fieldBlockMobile}>
          <div className={styles.fieldLabelMobile}>{t('status')}</div>
          <div className={styles.fieldValueMobile}>{getStatus(user) || '-'}</div>
        </div>

        <div className={styles.fieldBlockMobile}>
          <div className={styles.fieldLabelMobile}>{t('last-login-table-header')}</div>
          <div className={styles.fieldValueMobile}>{getLastLogin(user) || '-'}</div>
        </div>

        <div className={styles.actionsMobile} onClick={e => e.stopPropagation()}>
          <Button
            onClick={e => handleEdit(e, user.id)}
            fullWidth
            variant="contained"
            className="primary-btn"
            disableElevation
            sx={{ ...editStyleUser }}
          >
            {t('edit')}
          </Button>

          <Button
            onClick={() => handleView(user.id)}
            fullWidth
            variant="contained"
            className="primary-btn"
            disableElevation
            sx={{
              color: 'rgba(0, 0, 0, 0.87)',
              width: '100%',
              backgroundColor: 'rgba(98, 81, 55, 0.1)',
              '&:hover': { backgroundColor: 'rgba(102, 90, 71, 0.20)' },
              '&:disabled': { backgroundColor: '#665A47' },
              '&:focus': { backgroundColor: 'rgba(102, 90, 71, 0.20)' },
              fontFamily: 'Johnson Text',
              fontWeight: 600,
              fontSize: '12px',
              marginTop: '8px',
            }}
          >
            {t('view')}
          </Button>
        </div>
      </div>
    )
  }

  const renderCardMobileGroup = group => {
    return (
      <div
        key={group.id}
        className={styles.cardMobile}
        tabIndex={0}
      >
        <div className={styles.fieldBlockMobile}>
          <div className={styles.fieldLabelMobile}>{t('view-edit-column-group-id')}</div>
          <div className={styles.fieldValueMobile}>{group.id}</div>
        </div>

        <div className={styles.fieldBlockMobile}>
          <div className={styles.fieldLabelMobile}>{t('view-edit-column-group-name')}</div>
          <div className={styles.fieldValueMobile}>{group.displayName}</div>
        </div>

        <div className={styles.actionsMobile} onClick={e => e.stopPropagation()}>
          <Button
            onClick={e => handleEdit(e, group.id)}
            fullWidth
            variant="contained"
            className="primary-btn"
            disableElevation
            sx={{ ...editStyleGroup }}
          >
            {t('view-edit-column-group-edit-button')}
          </Button>

          <Button
            onClick={e => handleView(group.id)}
            fullWidth
            variant="contained"
            className="primary-btn"
            disableElevation
            sx={{
              color: 'rgba(0, 0, 0, 0.87)',
              width: '100%',
              backgroundColor: 'rgba(98, 81, 55, 0.1)',
              '&:hover': { backgroundColor: 'rgba(102, 90, 71, 0.20)' },
              '&:disabled': { backgroundColor: '#665A47' },
              '&:focus': { backgroundColor: 'rgba(102, 90, 71, 0.20)' },
              fontFamily: 'Johnson Text',
              fontWeight: 600,
              fontSize: '12px',
              marginTop: '8px',
            }}
          >
            {t('view-column-group-button')}
          </Button>

          <Button
            onClick={e => handleEditMembers(e, group.id)}
            fullWidth
            variant="contained"
            className="primary-btn"
            disableElevation
            sx={{
              color: 'rgba(0, 0, 0, 0.87)',
              width: '100%',
              backgroundColor: 'rgba(98, 81, 55, 0.1)',
              '&:hover': { backgroundColor: 'rgba(102, 90, 71, 0.20)' },
              '&:disabled': { backgroundColor: '#665A47' },
              '&:focus': { backgroundColor: 'rgba(102, 90, 71, 0.20)' },
              fontFamily: 'Johnson Text',
              fontWeight: 600,
              fontSize: '12px',
              marginTop: '8px',
            }}
          >
            {t('view-edit-column-group-member-button-mobile')}
          </Button>
        </div>
      </div>
    )
  }

  const renderMobile = () => (
    <div className={styles.containerMobile}>
      {(areLoading || (items && items.length > 0)) && (
        <div className={styles.listWrapperMobile}>
          <p className={styles.resultsMobile}>
            {items?.length || 0} {t('results')}
          </p>

          {areLoading ? (
            <div className={styles.loaderContainerMobile}>
              <div className={styles.loadingMobile}>{t('loading')}</div>
              <CircularProgress sx={{ color: 'black' }} />
            </div>
          ) : (
            <div className={styles.cardListMobile}>
              {sortedItems
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(entity =>
                  type === 'user'
                    ? renderCardMobileUser(entity)
                    : renderCardMobileGroup(entity)
                )}
            </div>
          )}

          {(items?.length || 0) > 10 && (
            <div className={styles.paginationMobile}>
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 100]}
                count={items?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )

  return <>{isMobileView ? renderMobile() : renderDesktop()}</>
}

export default EntityList
